import { NgxCurrencyConfig } from 'ngx-currency';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';

import { AuthFacade, UserPermission, UserSettingsFacade } from '@mitsubishi/core';
import { AccessoryType, ProjectFacade, ProjectSelectionsFacade } from '@mitsubishi/project/data-access';
import { Currency, PriceTypeEnum, Prices } from '@mitsubishi/shared/data-access';
import { PriceUtils, RxjsUtils } from '@mitsubishi/utils';

import { isNil } from '@angular-ru/cdk/utils';

@Component({
  selector: 'apm-accessory-price',
  templateUrl: './accessory-price.component.html',
  styleUrls: ['./accessory-price.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccessoryPriceComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly prices = input.required<Prices>();
  readonly selectedPrice = input.required<string | PriceTypeEnum>();
  readonly accessoryId = input<string>();
  readonly accessoryType = input<string>();
  readonly customCurrency = input<string | null>();
  readonly editable = input(false);
  readonly applyCurrencyConversion = input(false);

  readonly priceInput = viewChild('priceInput', { read: ElementRef });

  #authFacade = inject(AuthFacade);
  #userSettingsFacade = inject(UserSettingsFacade);
  #projectFacade = inject(ProjectFacade);
  #selectionFacade = inject(ProjectSelectionsFacade);

  canMenageRfq = this.#authFacade.hasPermission(UserPermission.ManageCustomAndRfq);

  viewInitialized = false;
  accessoryTypeEnum = AccessoryType;
  rfqPrice = 0;
  readonly showPrices = this.#userSettingsFacade.showPrices;

  readonly accessoryPrice = computed(() =>
    PriceUtils.getPrice(this.prices(), this.selectedPrice(), {
      applyCurrencyConversion: this.applyCurrencyConversion(),
    })
  );
  readonly currentSelectionOrStudyCurrencySymbol = this.#projectFacade.currentSelectionOrStudyCurrencySymbol;

  readonly priceCurrencySymbol = computed(() => {
    if (this.customCurrency()) {
      return this.customCurrency();
    }

    const pricesCurrencyId = this.prices().currencyId;
    return pricesCurrencyId ? Currency[pricesCurrencyId] : this.currentSelectionOrStudyCurrencySymbol();
  });
  readonly currencyMaskOptions = computed<Partial<NgxCurrencyConfig>>(() => {
    return {
      prefix: `${this.priceCurrencySymbol()} `,
    };
  });

  private subs: SubSink = new SubSink();

  constructor() {
    effect(() => {
      this.addPriceInputEvents();
    });
  }

  ngOnInit(): void {
    this.rfqPrice =
      PriceUtils.getPrice(this.prices(), this.selectedPrice(), {
        applyCurrencyConversion: this.applyCurrencyConversion(),
      }) || 0;
  }

  private updatePrice() {
    const accessoryId = this.accessoryId();
    if (isNil(accessoryId)) {
      return;
    }
    this.#selectionFacade.updateAccessoryPrice(accessoryId, this.rfqPrice);
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    // Try to set up event listeners after view is initialized
    this.addPriceInputEvents();
  }

  private addPriceInputEvents() {
    if (!this.viewInitialized) {
      // If the view is not initialized, we cannot set up event listeners yet
      return;
    }

    if (this.canMenageRfq && this.editable() && this.accessoryType() === AccessoryType.RFQ) {
      this.subs.unsubscribe();

      // Check if priceInput is defined before using it
      const priceInputElement = this.priceInput();

      if (priceInputElement) {
        const inputEvents = ['focusout', 'keyup.Enter'];
        this.subs.sink = RxjsUtils.getMergedInputEventsObservable(priceInputElement, inputEvents, this.rfqPrice)
          .pipe(
            map(() => {
              return this.rfqPrice;
            }),
            distinctUntilChanged(),
            tap(() => this.updatePrice())
          )
          .subscribe();
      } else {
        console.warn('Price input element is not available yet');
      }
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
